import { create } from 'zustand';
import { getMeVehicleTrips } from '@/api/generated/me-vehicle-trips/me-vehicle-trips';
import type {
  FacilityFlowCoreModulesFleetDtosCurrentVehicleTripDto,
  FacilityFlowCoreModulesFleetDtosEndVehicleTripRequest,
  FacilityFlowCoreModulesFleetDtosMyVehicleTripDto,
  FacilityFlowCoreModulesFleetDtosMyVehicleTripStatusDto,
  FacilityFlowCoreModulesFleetDtosStartVehicleTripRequest,
  FacilityFlowCoreModulesFleetDtosTripDetailsSuggestionDto,
  FacilityFlowCoreModulesFleetDtosUnparkedTripDto,
} from '@/api/generated/models';
import { parseAxiosError, translateApiError } from '@/errors';
import { showApiError } from '@/errors/showApiError';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';
import { addIsoDays } from '@/utils/dateUtils';
import { DEFAULT_TRIP_END_RULES, tripEndRulesOf, tripsByLocalDay, type TripEndRules } from '@/utils/vehicleTrips';

export type UnparkedTrip = FacilityFlowCoreModulesFleetDtosUnparkedTripDto;
export type CurrentTrip = FacilityFlowCoreModulesFleetDtosCurrentVehicleTripDto;
export type MyTrip = FacilityFlowCoreModulesFleetDtosMyVehicleTripDto;
export type TripSuggestion = FacilityFlowCoreModulesFleetDtosTripDetailsSuggestionDto;

const api = getMeVehicleTrips();

interface MyVehicleTripsState {
  /** The employee's running trip (driver) or ride (passenger), separate from the time clock (FAFD-68). */
  current: CurrentTrip | null;
  allowPrivateTrips: boolean;
  /** What ending a trip requires, as the tenant set it. */
  endRules: TripEndRules;
  /** Trips left without a reading that the employee still has to park, oldest first. */
  unparked: UnparkedTrip[];
  /** Own trips and rides of the loaded period (tab "Fahrten"). */
  trips: MyTrip[];
  tripsLoading: boolean;
  tripsError: string | null;
  /** Own trips and rides touching one local day — the read line of the day view. */
  dayTrips: MyTrip[];
  dayTripsDate: string | null;
  saving: boolean;
  parking: boolean;

  /** Reloads the running trip; on failure keeps the last state — the backend decides anyway. */
  fetchCurrent: () => Promise<CurrentTrip | null>;
  fetchTrips: (from: string, to: string) => Promise<void>;
  /** Loads the trips of one local day, incl. one running across midnight. Failures stay silent. */
  fetchDayTrips: (day: string) => Promise<void>;
  /** Reloads the list; on failure keeps the last one — the backend still refuses to drive. */
  fetchMyUnparked: () => Promise<UnparkedTrip[]>;
  /**
   * Start, destination and purpose suggested for a departure at `at` (ISO; now without) — the
   * start also from where `vehicleId` was left today. Null on failure — the fields then start
   * from the vehicle and the backend fills what stays empty.
   */
  fetchSuggestion: (at?: string, vehicleId?: string) => Promise<TripSuggestion | null>;
  /** Starts a trip or ride now; returns whether it started. */
  startTrip: (request: FacilityFlowCoreModulesFleetDtosStartVehicleTripRequest) => Promise<boolean>;
  /** Ends the own trip or ride now; returns whether it ended. */
  endTrip: (request: FacilityFlowCoreModulesFleetDtosEndVehicleTripRequest) => Promise<boolean>;
  /** Enters the reading once; returns whether it was saved. */
  park: (entryId: string, odometerKm: number) => Promise<boolean>;
  /** After a stamp or parking that changed a trip: status, open items and the lists catch up. */
  refreshAfterStamp: () => void;
}

function statusOf(dto: FacilityFlowCoreModulesFleetDtosMyVehicleTripStatusDto) {
  return { current: dto.current ?? null, allowPrivateTrips: dto.allowPrivateTrips ?? false, endRules: tripEndRulesOf(dto) };
}

let lastPeriod: { from: string; to: string } | null = null;

export const useMyVehicleTripsStore = create<MyVehicleTripsState>((set, get) => ({
  current: null,
  allowPrivateTrips: false,
  endRules: DEFAULT_TRIP_END_RULES,
  unparked: [],
  trips: [],
  tripsLoading: false,
  tripsError: null,
  dayTrips: [],
  dayTripsDate: null,
  saving: false,
  parking: false,

  fetchCurrent: async () => {
    try {
      const status = statusOf(await api.getApiV1MeVehicleTripsCurrent());
      set(status);
      return status.current;
    } catch {
      // why: only drives the trip menu and the preselection — stamping must not fail on it, and
      // the backend still refuses what is not allowed (fleet.trips.alreadyDriving, noRunningTrip).
      return get().current;
    }
  },

  fetchTrips: async (from, to) => {
    lastPeriod = { from, to };
    set({ tripsLoading: true, tripsError: null });
    try {
      const data = await api.getApiV1MeVehicleTrips({ from, to });
      set({ trips: data.trips ?? [], tripsLoading: false });
    } catch (err) {
      set({ tripsError: translateApiError(parseAxiosError(err)), tripsLoading: false });
    }
  },

  fetchDayTrips: async (day) => {
    try {
      // A trip that started the evening before still belongs to this day.
      const data = await api.getApiV1MeVehicleTrips({ from: addIsoDays(day, -1), to: day });
      set({ dayTrips: tripsByLocalDay(data.trips ?? [], new Date()).get(day) ?? [], dayTripsDate: day });
    } catch {
      // why: only a read line under the day — the tab "Fahrten" shows the error if it persists.
      set({ dayTrips: [], dayTripsDate: day });
    }
  },

  fetchMyUnparked: async () => {
    try {
      const data = await api.getApiV1MeVehicleTripsUnparked();
      set({ unparked: data });
      return data;
    } catch {
      // why: only a hint for the dialog and the dashboard — the backend still refuses to drive
      // (fleet.trips.vehicleNotParked).
      return get().unparked;
    }
  },

  fetchSuggestion: async (at, vehicleId) => {
    try {
      return await api.getApiV1MeVehicleTripsSuggestion({ ...(at ? { at } : {}), ...(vehicleId ? { vehicleId } : {}) });
    } catch {
      // why: only a prefill — without it the driver types the details as before.
      return null;
    }
  },

  startTrip: async (request) => {
    set({ saving: true });
    try {
      set({ ...statusOf(await api.postApiV1MeVehicleTripsStart(request)), saving: false });
      useToastStore.getState().addToast(i18n.t('vehicles:trip.started'), 'success');
      get().refreshAfterStamp();
      return true;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return false;
    }
  },

  endTrip: async (request) => {
    set({ saving: true });
    try {
      set({ ...statusOf(await api.postApiV1MeVehicleTripsCurrentEnd(request)), saving: false });
      useToastStore.getState().addToast(i18n.t('vehicles:trip.ended'), 'success');
      get().refreshAfterStamp();
      return true;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      // Ended meanwhile (e.g. by the administration): the menu is out of date.
      await get().fetchCurrent();
      return false;
    }
  },

  park: async (entryId, odometerKm) => {
    set({ parking: true });
    try {
      await api.postApiV1MeVehicleTripsEntryIdPark(entryId, { odometerKm });
      set((s) => ({ unparked: s.unparked.filter((t) => t.entryId !== entryId), parking: false }));
      useToastStore.getState().addToast(i18n.t('vehicles:park.saved'), 'success');
      get().refreshAfterStamp();
      return true;
    } catch (err) {
      set({ parking: false });
      showApiError(err, { skipIfFieldErrors: false });
      // Parked meanwhile (by the next driver or the administration): the list is out of date.
      await get().fetchMyUnparked();
      return false;
    }
  },

  refreshAfterStamp: () => {
    void get().fetchCurrent();
    void get().fetchMyUnparked();
    if (lastPeriod) void get().fetchTrips(lastPeriod.from, lastPeriod.to);
    const day = get().dayTripsDate;
    if (day) void get().fetchDayTrips(day);
  },
}));
